import ActivityIcon from "@Components/activity/ActivityIcon";
import HorizontalBar from "@Components/activity/HorizontalBar";
import VideoTag from "@Components/activity/VideoTag";
import VideoWrapper from "@Components/activity/VideoWrapper";
import DynamicTable from "@Components/shared/DynamicTable";
import LargeSpinner from "@Components/shared/LargeSpinner";
import PageHelmet from "@Components/shared/PageHelmet";
import MetricsPercentileGraph from "@Components/activity/MetricsPercentileGraph";
import { IActivityDetails } from "@Models/IActivityDetails";
import { getActivityMetric } from "@Utils";
import { Alert, Button, Card, Col, Empty, Row } from "antd";
import Title from "antd/es/typography/Title";
import moment from "moment";
import React, { memo } from "react";
import useStudentTaskSubmission from "./hooks/useStudentTaskSubmission";
import { IAssessmentViewProps } from "./types";
import { CheckOutlined } from "@ant-design/icons";

const StudentTaskSubmissionView: React.FC<IAssessmentViewProps> = () => {
    const {
        isDisabled,
        isFetching,
        isLoading,
        isSubmitting,
        tab,
        tableData,
        videoActivity,

        onRowClick,
        onSubmit,
    } = useStudentTaskSubmission();

    const speed = getActivityMetric("duration-percentile", videoActivity);
    const precision = getActivityMetric("precision", videoActivity);
    const smoothness = getActivityMetric("smoothness", videoActivity);

    const submissionTip = isDisabled
        ? "Once all of the tasks above have been fulfilled you will be able to submit them to this assessment."
        : "All tasks completed. You may now submit to this assessment.";

    return (
        <article className={`student-task-submission-view ${tab}-tab`}>
            <PageHelmet name={"Assessments Student Task Submission View"} />
            <section>
                {isLoading && <LargeSpinner />}
                {!isLoading && (
                    <Row gutter={[16, 16]} className="student-assessment-view-top">
                        <Col xs={24}>
                            <Card className="newUI-subtle">
                                <DynamicTable
                                    style={{ textAlign: "center" }}
                                    columnData={tableData.taskTable.columnData}
                                    dataSource={tableData.taskTable.data}
                                    isFetching={isFetching}
                                    isLoading={tableData.taskTable.isLoading}
                                    rowKey="taskId"
                                />
                                <Row style={{ alignItems: "center", paddingTop: "20px" }}>
                                    <Alert description={submissionTip} type="info" showIcon />
                                    <Button
                                        icon={<CheckOutlined />}
                                        className="secondary"
                                        style={{ marginLeft: "20px", flex: "auto" }}
                                        disabled={isDisabled || isLoading || isSubmitting}
                                        onClick={onSubmit}
                                        loading={isLoading || isSubmitting}
                                        htmlType="submit"
                                    >
                                        Submit Tasks
                                    </Button>
                                </Row>
                            </Card>
                        </Col>
                        <Col xs={24} md={24} lg={12} xl={12} xxl={12}>
                            <Card className="newUI-subtle full-width" hoverable={false}>
                                {tableData.activityList.isFetched && tableData.activityList.data.length === 0 ? (
                                    <Empty description="No activities found." />
                                ) : (
                                    <DynamicTable
                                        className="student-assessment-view-table"
                                        columnData={tableData.activityList.columnData}
                                        dataSource={tableData.activityList.data}
                                        isFetching={isFetching}
                                        isLoading={tableData.activityList.isLoading}
                                        onRow={(record: IActivityDetails, _) => ({
                                            onClick: () => onRowClick(record),
                                        })}
                                        rowKey="activityInstanceId"
                                        rowSelection={tableData.activityList.rowSelection}
                                        scroll={!!videoActivity ? {} : { y: 240 }}
                                    />
                                )}
                            </Card>
                        </Col>
                        <Col xs={24} md={24} lg={12} xl={12} xxl={12} className="student-assessment-view-video">
                            {!videoActivity && (
                                <Alert
                                    description="Select an activity from the list to review the statistics and video if available."
                                    type="info"
                                    showIcon
                                />
                            )}
                            {videoActivity && videoActivity?.mediaUrl !== null ? (
                                <div>
                                    <div className="empty-video newUI-glass" style={{ marginTop: "5px" }}>
                                        <ActivityIcon
                                            categoryName={videoActivity?.categoryName}
                                            style={{ marginRight: "10px" }}
                                            size={24}
                                        />
                                        <span>{videoActivity?.activityLabel}</span>
                                        <VideoTag mediaStatus={videoActivity?.mediaStatus} />
                                        <span style={{ marginLeft: "20px" }}>
                                            {moment(videoActivity?.creationDate).format("MMM Do YYYY, h:mm A")}
                                        </span>
                                    </div>
                                    <VideoWrapper activity={videoActivity} withoutComments />
                                </div>
                            ) : (
                                videoActivity &&
                                videoActivity?.mediaUrl === null && (
                                    <div>
                                        <div className="empty-video newUI-glass" style={{ marginTop: "5px" }}>
                                            <ActivityIcon
                                                categoryName={videoActivity?.categoryName}
                                                style={{ marginRight: "10px" }}
                                                size={24}
                                            />
                                            <span>{videoActivity?.activityLabel}</span>
                                            <VideoTag mediaStatus={videoActivity?.mediaStatus} />
                                            <span style={{ marginLeft: "20px" }}>
                                                {moment(videoActivity?.creationDate).format("MMM Do YYYY, h:mm A")}
                                            </span>
                                        </div>
                                    </div>
                                )
                            )}

                            {videoActivity !== undefined && videoActivity !== null && (
                                <div className={"newUI-raised"}>
                                    <Title level={4}>Completion Time</Title>
                                    <span>Operating duration against the average</span>
                                    <div>
                                        <HorizontalBar
                                            values={[
                                                getActivityMetric("duration", videoActivity),
                                                getActivityMetric("duration-median", videoActivity),
                                            ]}
                                            labels={["User duration", "Global average"]}
                                            timeFormat={true}
                                            maxBarWidth={30}
                                            height={250}
                                        />
                                    </div>
                                </div>
                            )}

                            {videoActivity !== undefined &&
                                videoActivity !== null &&
                                (smoothness > 0 || precision > 0 || speed > 0) && (
                                    <div style={{ marginTop: "40px" }}>
                                        <Title level={4} style={{ width: "100%" }}>
                                            Metrics
                                        </Title>
                                        <span>Smoothness, speed and precision</span>
                                        <div>
                                            <MetricsPercentileGraph
                                                height={300}
                                                radius={0.9}
                                                activity={videoActivity}
                                            />
                                        </div>
                                    </div>
                                )}
                        </Col>
                    </Row>
                )}
            </section>
        </article>
    );
};

export default memo(StudentTaskSubmissionView);
